import { useState } from 'react';
import { Calculator, GitCompare, TrendingUp, Calendar } from 'lucide-react';
import type { TabId } from './types/erlang';
import ErlangCTab from './components/ErlangCTab';
import ErlangATab from './components/ErlangATab';
import SensitivityTab from './components/SensitivityTab';
import DayPlannerTab from './components/DayPlannerTab';

const TABS: { id: TabId; label: string; icon: typeof Calculator; description: string }[] = [
  { id: 'erlangC', label: 'Erlang C', icon: Calculator, description: 'Enhanced staffing calculator' },
  { id: 'erlangA', label: 'Erlang A', icon: GitCompare, description: 'Abandonment modeling + C vs A comparison' },
  { id: 'sensitivity', label: 'Power of One', icon: TrendingUp, description: 'What happens when you lose agents' },
  { id: 'dayPlanner', label: 'Day Planner', icon: Calendar, description: 'Interval-by-interval staffing' },
];

export default function App() {
  const [activeTab, setActiveTab] = useState<TabId>('erlangC');

  return (
    <div className="min-h-screen bg-navy-950">
      {/* Header */}
      <header className="border-b border-gray-800/50 bg-navy-950/95 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-white">
                Erlang Suite
                <span className="text-cyan-500 ml-1.5 text-sm font-medium">by WFM Labs</span>
              </h1>
              <p className="text-xs text-gray-500 mt-0.5">Advanced Erlang C/A Calculator with Power of One Analysis & Day Planning</p>
            </div>
            <a
              href="https://wfmlabs.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-500 hover:text-gray-300 transition-colors hidden sm:block"
            >
              wfmlabs.com
            </a>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <nav className="flex gap-1 -mb-px">
            {TABS.map(({ id, label, icon: Icon, description }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`group flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
                  activeTab === id
                    ? 'border-cyan-500 text-white'
                    : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-600'
                }`}
              >
                <Icon size={16} className={activeTab === id ? 'text-cyan-500' : 'text-gray-600 group-hover:text-gray-400'} />
                <span>{label}</span>
                <span className="hidden md:inline text-xs text-gray-600">— {description}</span>
              </button>
            ))}
          </nav>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {activeTab === 'erlangC' && <ErlangCTab />}
        {activeTab === 'erlangA' && <ErlangATab />}
        {activeTab === 'sensitivity' && <SensitivityTab />}
        {activeTab === 'dayPlanner' && <DayPlannerTab />}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800/30 py-4 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center text-xs text-gray-600">
          WFM Labs Erlang Suite — Premium Builder Tool &middot; Erlang C/A with numerically stable recursion
        </div>
      </footer>
    </div>
  );
}
